import { useCallback, useEffect, useMemo, useRef, useState, type JSX } from 'react';

import {
  Combobox,
  EloIcon,
  EloInputFieldLabel,
  EloInputHelperText,
  EloScrollbar,
  Field,
  Portal,
  chakra,
  createListCollection,
} from '@plurall/elo-v3';

import {
  timeInputDropdownRecipe,
  timeInputEndContainerRecipe,
  timeInputFieldRecipe,
  timeInputTriggerRecipe,
  timeInputWrapperRecipe,
  timeSlotItemRecipe,
} from './TimeInput.recipe';
import { filterTimeSlotItems, isTimeAfter, isValidTime } from './timeUtils';

const FieldWrapper = chakra('div', timeInputWrapperRecipe);
const TimeInputField = chakra('input', timeInputFieldRecipe);
const EndContainer = chakra('div', timeInputEndContainerRecipe);
const TimeInputTrigger = chakra(Combobox.Trigger, timeInputTriggerRecipe);
const DropdownContent = chakra('div', timeInputDropdownRecipe);
const TimeSlotItem = chakra('div', timeSlotItemRecipe);

type Props = {
  value: string | null;
  onChange: (timeValue: string | null) => void;
  label?: string;
  helperText?: string;
  disabled?: boolean;
  afterTime?: string | null;
};

export function TimeInput({ value, onChange, label, helperText, disabled, afterTime }: Props): JSX.Element {
  const [inputValue, setInputValue] = useState(() => value ?? '');
  const contentRef = useRef<HTMLDivElement>(null);

  const prevValueRef = useRef(value);
  useEffect(() => {
    if (value !== prevValueRef.current) {
      prevValueRef.current = value;
      setInputValue(value ?? '');
    }
  }, [value]);

  const filteredItems = useMemo(() => filterTimeSlotItems(inputValue, afterTime), [inputValue, afterTime]);
  const collection = useMemo(() => createListCollection({ items: filteredItems }), [filteredItems]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key >= '0' && event.key <= '9') {
        event.preventDefault();

        const digits = inputValue.replace(/:/g, '');
        if (digits.length >= 4) return;

        const newDigits = digits + event.key;
        // Auto-insert colon after 2nd digit: "13" → "13:", "130" → "13:0"
        const formatted = newDigits.length >= 2 ? `${newDigits.slice(0, 2)}:${newDigits.slice(2)}` : newDigits;

        if (newDigits.length === 4) {
          if (isValidTime(newDigits) && isTimeAfter(formatted, afterTime)) {
            setInputValue(formatted);
            onChange(formatted);
          } else {
            setInputValue('');
            onChange(null);
          }
        } else {
          setInputValue(formatted);
        }
      } else if (event.key === 'Backspace') {
        event.preventDefault();
        const digits = inputValue.replace(/:/g, '');
        const newDigits = digits.slice(0, -1);
        const formatted = newDigits.length >= 2 ? `${newDigits.slice(0, 2)}:${newDigits.slice(2)}` : newDigits;
        setInputValue(formatted);
        if (newDigits.length < 4) onChange(null);
      }
    },
    [inputValue, onChange, afterTime]
  );

  const handleValueChange = useCallback(
    (details: { value: string[] }) => {
      const selected = details.value[0];
      if (selected) {
        setInputValue(selected);
        onChange(selected);
      }
    },
    [onChange]
  );

  const handleHighlightChange = useCallback(() => {
    requestAnimationFrame(() => {
      const highlighted = contentRef.current?.querySelector('[data-highlighted]');
      highlighted?.scrollIntoView({ block: 'center' });
    });
  }, []);

  return (
    <Field.Root
      disabled={disabled}
      display='flex'
      flexDirection='column'
      gap='gap.3xsmall'>
      {label && <EloInputFieldLabel disabled={disabled}>{label}</EloInputFieldLabel>}

      <Combobox.Root
        collection={collection}
        inputValue={inputValue}
        onInputValueChange={() => {
          // Controlled — inputValue is managed via our keydown handler
        }}
        value={value ? [value] : []}
        onValueChange={handleValueChange}
        onHighlightChange={handleHighlightChange}
        openOnClick
        loopFocus
        disabled={disabled}
        positioning={{ gutter: 6 }}>
        {/* Combobox.Control wraps the full field so the positioner aligns to its width */}
        <Combobox.Control asChild>
          <FieldWrapper>
            <Combobox.Input asChild>
              <TimeInputField
                onKeyDown={handleKeyDown}
                placeholder='00:00'
              />
            </Combobox.Input>
            <EndContainer>
              <TimeInputTrigger type='button'>
                <EloIcon
                  icon='clock'
                  size='small'
                />
              </TimeInputTrigger>
            </EndContainer>
          </FieldWrapper>
        </Combobox.Control>

        <Portal>
          <Combobox.Positioner>
            <Combobox.Content
              unstyled
              asChild>
              <DropdownContent>
                <div ref={contentRef}>
                  <EloScrollbar
                    orientation='vertical'
                    size='medium'
                    maxHeight={240}>
                    {filteredItems.map((item) => (
                      <Combobox.Item
                        key={item.value}
                        item={item}
                        unstyled
                        asChild>
                        <TimeSlotItem>
                          <Combobox.ItemText>{item.label}</Combobox.ItemText>
                        </TimeSlotItem>
                      </Combobox.Item>
                    ))}
                  </EloScrollbar>
                </div>
              </DropdownContent>
            </Combobox.Content>
          </Combobox.Positioner>
        </Portal>
      </Combobox.Root>

      {helperText && <EloInputHelperText text={helperText} />}
    </Field.Root>
  );
}
